import { useEffect, useState } from "react";
import { Clock, Paperclip, Download, Eye, X } from "@/components/icons";
import api, { formatErr } from "../../../lib/api";
import { toast } from "sonner";
import Portal from "../../../components/Portal";
import { Card, SectionHeader, Empty, fmtDate, fmtTime } from "./common";

const TYPE_LABEL = {
  dx: "Diagnóstico",
  appt: "Cita",
  rx: "Receta",
  file: "Archivo",
  edit: "Edición",
};

const TYPE_TONE = {
  dx: "bg-black text-white",
  appt: "bg-[#F2F3F5] text-black",
  rx: "bg-[#F2F3F5] text-black/80",
  file: "bg-[#F2F3F5] text-black/80",
  edit: "bg-[#F2F3F5] text-black/60",
};

const FIELD_LABEL = {
  full_name: "Nombre",
  document_id: "Documento",
  birth_date: "Fecha de nacimiento",
  gender: "Género",
  phone: "Teléfono",
  email: "Email",
  address: "Dirección",
  blood_type: "Tipo de sangre",
  allergies: "Alergias",
  notes: "Notas",
  photo_url: "Foto",
  office_id: "Oficina",
};

const ROLE_LABEL = {
  doctor: "Doctor",
  secretary: "Secretaria",
  admin: "Admin",
};

const fmt = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const s = String(v);
  return s.length > 60 ? s.slice(0, 57) + "…" : s;
};

export default function HistoryTab({ patientId, diagnoses, appointments, prescriptions }) {
  const [files, setFiles] = useState([]);
  const [audits, setAudits] = useState([]);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/patients/${patientId}/files`)
      .then((r) => {
        if (!cancelled) setFiles(r.data || []);
      })
      .catch(() => {});
    api
      .get(`/patients/${patientId}/audits`)
      .then((r) => {
        if (!cancelled) setAudits(r.data || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  const downloadFile = async (fileId) => {
    try {
      const r = await api.get(`/patients/${patientId}/files/${fileId}`);
      const { data_base64, mime_type, name } = r.data;
      const bytes = Uint8Array.from(atob(data_base64), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: mime_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Error al descargar");
    }
  };

  const items = [
    ...diagnoses.map((d) => ({
      id: `dx-${d.id}`,
      type: "dx",
      date: d.created_at,
      title: d.diagnosis || "Diagnóstico",
      sub: d.symptoms || d.notes || "—",
      raw: d,
    })),
    ...appointments.map((a) => ({
      id: `appt-${a.id}`,
      type: "appt",
      date: a.date,
      title: a.reason || "Cita",
      sub: a.status || "—",
      raw: a,
    })),
    ...prescriptions.map((rx) => ({
      id: `rx-${rx.id}`,
      type: "rx",
      date: rx.created_at,
      title: rx.diagnosis || "Receta",
      sub: `${rx.items?.length || 0} medicamento(s)`,
      raw: rx,
    })),
    ...files.map((f) => ({
      id: `file-${f.id}`,
      type: "file",
      date: f.created_at,
      title: f.name,
      sub: `${mimeLabel(f.mime_type)} · ${humanSize(f.size)}${
        f.uploaded_by_name ? ` · ${f.uploaded_by_name}` : ""
      }`,
      action: () => downloadFile(f.id),
      raw: f,
    })),
    ...audits.map((au) => {
      const changesText = (au.changes || [])
        .map(
          (c) =>
            `${FIELD_LABEL[c.field] || c.field}: ${fmt(c.old)} → ${fmt(c.new)}`,
        )
        .join(" · ");
      const actor = au.actor_name
        ? `${au.actor_name} (${ROLE_LABEL[au.actor_role] || au.actor_role || "?"})`
        : ROLE_LABEL[au.actor_role] || "Sistema";
      return {
        id: `edit-${au.id}`,
        type: "edit",
        date: au.created_at,
        title: `Editado por ${actor}`,
        sub: changesText || "Sin cambios",
        raw: au,
      };
    }),
  ].sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <Card>
      <SectionHeader icon={Clock} title="Historial clínico completo" />
      {items.length === 0 ? (
        <Empty>Sin historial</Empty>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="history-table">
            <thead>
              <tr>
                {["Fecha", "Tipo", "Evento", "Detalle", ""].map((h, i) => (
                  <th
                    key={h + i}
                    className="pb-2 px-3 first:pl-0 last:pr-0 text-[10px] uppercase tracking-widest text-black/40 font-semibold text-left"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr
                  key={it.id}
                  className="border-t border-black/[0.05] text-black hover:bg-black/[0.02] transition"
                  data-testid={`history-row-${it.type}`}
                >
                  <td className="py-3 pr-3 whitespace-nowrap">
                    <div className="text-[13px] font-semibold text-black">{fmtDate(it.date)}</div>
                    <div className="text-[11px] text-black/45">
                      {fmtTime(it.date)}
                    </div>
                  </td>
                  <td className="py-3 px-3">
                    <span
                      className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-1 rounded-md inline-flex items-center gap-1 ${TYPE_TONE[it.type]}`}
                    >
                      {it.type === "file" && <Paperclip className="w-3 h-3" strokeWidth={1.75} weight="regular" />}
                      {TYPE_LABEL[it.type]}
                    </span>
                  </td>
                  <td className="py-3 px-3 text-[13px] font-semibold text-black max-w-[280px] truncate">
                    {it.title}
                  </td>
                  <td className="py-3 px-3 text-[13px] text-black/60 max-w-[420px] truncate">{it.sub}</td>
                  <td className="py-3 pl-3 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => setDetail(it)}
                        data-testid={`history-view-${it.id}`}
                        title="Ver detalle"
                        className="w-7 h-7 rounded-md hover:bg-black/[0.05] text-black/70 hover:text-black inline-flex items-center justify-center transition"
                      >
                        <Eye className="w-3.5 h-3.5" strokeWidth={1.75} weight="regular" />
                      </button>
                      {it.action && (
                        <button
                          onClick={it.action}
                          data-testid={`history-download-${it.id}`}
                          title="Descargar archivo"
                          className="w-7 h-7 rounded-md hover:bg-black/[0.05] text-black/70 hover:text-black inline-flex items-center justify-center transition"
                        >
                          <Download className="w-3.5 h-3.5" strokeWidth={1.75} weight="regular" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {detail && (
        <HistoryDetailModal item={detail} onClose={() => setDetail(null)} />
      )}
    </Card>
  );
}

function HistoryDetailModal({ item, onClose }) {
  const r = item.raw || {};
  return (
    <Portal>
      <div
        className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 fade-up"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose?.();
        }}
      >
        <div
          onMouseDown={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl border border-black/[0.05] shadow-2xl w-full max-w-xl max-h-[92vh] overflow-hidden flex flex-col"
          data-testid="history-detail-modal"
        >
          <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3 border-b border-black/[0.05]">
            <div>
              <div className="text-[10px] uppercase tracking-widest font-semibold text-black/45">
                {TYPE_LABEL[item.type]} · {fmtDate(item.date)} · {fmtTime(item.date)}
              </div>
              <h3 className="text-[15px] font-semibold text-black mt-0.5">
                {item.title}
              </h3>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-lg hover:bg-black/[0.05] flex items-center justify-center text-black/60 transition"
            >
              <X className="w-4 h-4" strokeWidth={1.75} weight="regular" />
            </button>
          </div>

          <div className="p-5 space-y-3 overflow-y-auto no-scrollbar flex-1">
            {item.type === "dx" && (
              <>
                <Field label="Síntomas" value={r.symptoms} />
                <Field label="Tratamiento" value={r.treatment} />
                <Field label="Notas" value={r.notes} multiline />
                <Field label="Exploración física" value={r.physical_exam} multiline />
              </>
            )}
            {item.type === "appt" && (
              <>
                <Field label="Motivo" value={r.reason} />
                <Field label="Estado" value={r.status} />
                <Field label="Duración" value={r.duration_min ? `${r.duration_min} min` : null} />
                {r.patient_confirmation && (
                  <Field label="Confirmación del paciente" value={r.patient_confirmation} />
                )}
                {r.arrived_at && (
                  <Field label="Paciente llegó" value={`${fmtDate(r.arrived_at)} · ${fmtTime(r.arrived_at)}`} />
                )}
              </>
            )}
            {item.type === "rx" && (
              <>
                {r.notes && <Field label="Notas" value={r.notes} multiline />}
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-black/45 mb-1.5">
                    Medicamentos
                  </div>
                  <ul className="space-y-1 bg-[#F2F3F5] rounded-lg p-3">
                    {(r.items || []).map((m, i) => (
                      <li key={i} className="text-[13px]">
                        <span className="font-semibold text-black">{m.medication}</span>{" "}
                        <span className="text-black/60">
                          · {m.dosage} · {m.frequency}
                          {m.duration ? ` · ${m.duration}` : ""}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}
            {item.type === "file" && (
              <>
                <Field label="Nombre" value={r.name} />
                <Field label="Tipo" value={mimeLabel(r.mime_type)} />
                <Field label="Tamaño" value={humanSize(r.size)} />
                {r.uploaded_by_name && (
                  <Field label="Subido por" value={r.uploaded_by_name} />
                )}
              </>
            )}
            {item.type === "edit" && (
              <div>
                <div className="text-[10px] uppercase tracking-widest font-semibold text-black/45 mb-1.5">
                  Cambios
                </div>
                <div className="bg-[#F2F3F5] rounded-lg divide-y divide-black/[0.05]">
                  {(r.changes || []).map((c, i) => (
                    <div key={i} className="p-3">
                      <div className="text-[10px] font-semibold uppercase tracking-widest text-black/45">
                        {FIELD_LABEL[c.field] || c.field}
                      </div>
                      <div className="text-[13px] mt-1">
                        <span className="text-red-600 line-through">
                          {fmt(c.old)}
                        </span>{" "}
                        <span className="text-black/40">→</span>{" "}
                        <span className="text-black font-semibold">
                          {fmt(c.new)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Portal>
  );
}

function Field({ label, value, multiline = false }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest font-semibold text-black/45">
        {label}
      </div>
      <div
        className={`text-[13px] text-black mt-0.5 ${multiline ? "whitespace-pre-line" : ""}`}
      >
        {value}
      </div>
    </div>
  );
}

function mimeLabel(mime) {
  if (!mime) return "Archivo";
  if (mime.startsWith("image/")) return "Imagen";
  if (mime === "application/pdf") return "PDF";
  if (mime.includes("word")) return "Word";
  if (mime === "text/plain") return "Texto";
  if (mime === "text/csv") return "CSV";
  return mime.split("/").pop();
}

function humanSize(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
